/**
 * Safe arithmetic formula evaluator for the serializable design format. Ported
 * verbatim from the application so the backoffice can render %-driven geometry
 * in moderation previews WITHOUT `eval`/`Function` (a submitted design is
 * untrusted input). Keep in sync with
 * `app/projects/application/src/entities/designs/formulaEval.ts`.
 */

type Vars = Record<string, number>;

const FUNCS: Record<string, (args: number[]) => number> = {
  min: (a) => Math.min(...a),
  max: (a) => Math.max(...a),
  clamp: ([x, lo, hi]) => Math.max(lo, Math.min(hi, x)),
  round: ([x]) => Math.round(x),
  floor: ([x]) => Math.floor(x),
  ceil: ([x]) => Math.ceil(x),
  abs: ([x]) => Math.abs(x),
  sqrt: ([x]) => Math.sqrt(x),
};

type Tok =
  | { t: "num"; v: number }
  | { t: "id"; v: string }
  | { t: "op"; v: string }
  | { t: "lp" }
  | { t: "rp" }
  | { t: "comma" };

const OPS = [">=", "<=", "==", "!=", ">", "<", "+", "-", "*", "/", "%"];

function tokenize(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") {
      i++;
      continue;
    }
    if (c === "(") {
      toks.push({ t: "lp" });
      i++;
      continue;
    }
    if (c === ")") {
      toks.push({ t: "rp" });
      i++;
      continue;
    }
    if (c === ",") {
      toks.push({ t: "comma" });
      i++;
      continue;
    }
    if (
      (c >= "0" && c <= "9") ||
      (c === "." && /[0-9]/.test(src[i + 1] ?? ""))
    ) {
      let j = i + 1;
      while (j < src.length && /[0-9.]/.test(src[j])) j++;
      const v = Number(src.slice(i, j));
      if (Number.isNaN(v)) throw new Error(`bad number at ${i}`);
      toks.push({ t: "num", v });
      i = j;
      continue;
    }
    if (/[a-zA-Z_]/.test(c)) {
      let j = i + 1;
      while (j < src.length && /[a-zA-Z0-9_]/.test(src[j])) j++;
      toks.push({ t: "id", v: src.slice(i, j) });
      i = j;
      continue;
    }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (op) {
      toks.push({ t: "op", v: op });
      i += op.length;
      continue;
    }
    throw new Error(`unexpected char '${c}' at ${i}`);
  }
  return toks;
}

class Parser {
  private pos = 0;
  constructor(
    private toks: Tok[],
    private vars: Vars,
  ) {}

  private peek(): Tok | undefined {
    return this.toks[this.pos];
  }
  private next(): Tok {
    const t = this.toks[this.pos];
    if (!t) throw new Error("unexpected end of formula");
    this.pos++;
    return t;
  }

  parse(): number {
    const v = this.comparison();
    if (this.pos !== this.toks.length) throw new Error("trailing tokens");
    return v;
  }

  private comparison(): number {
    const a = this.sum();
    const p = this.peek();
    if (p?.t === "op" && [">", ">=", "<", "<=", "==", "!="].includes(p.v)) {
      this.next();
      const b = this.sum();
      switch (p.v) {
        case ">":
          return a > b ? 1 : 0;
        case ">=":
          return a >= b ? 1 : 0;
        case "<":
          return a < b ? 1 : 0;
        case "<=":
          return a <= b ? 1 : 0;
        case "==":
          return a === b ? 1 : 0;
        case "!=":
          return a !== b ? 1 : 0;
      }
    }
    return a;
  }

  private sum(): number {
    let a = this.term();
    let p = this.peek();
    while (p?.t === "op" && (p.v === "+" || p.v === "-")) {
      this.next();
      const b = this.term();
      a = p.v === "+" ? a + b : a - b;
      p = this.peek();
    }
    return a;
  }

  private term(): number {
    let a = this.unary();
    let p = this.peek();
    while (p?.t === "op" && (p.v === "*" || p.v === "/" || p.v === "%")) {
      this.next();
      const b = this.unary();
      a = p.v === "*" ? a * b : p.v === "/" ? a / b : a % b;
      p = this.peek();
    }
    return a;
  }

  private unary(): number {
    const p = this.peek();
    if (p?.t === "op" && p.v === "-") {
      this.next();
      return -this.unary();
    }
    if (p?.t === "op" && p.v === "+") {
      this.next();
      return this.unary();
    }
    return this.primary();
  }

  private primary(): number {
    const tok = this.next();
    if (tok.t === "num") return tok.v;
    if (tok.t === "lp") {
      const v = this.comparison();
      const close = this.next();
      if (close.t !== "rp") throw new Error("expected )");
      return v;
    }
    if (tok.t === "id") {
      if (this.peek()?.t === "lp") {
        this.next();
        const args: number[] = [];
        if (this.peek()?.t !== "rp") {
          args.push(this.comparison());
          while (this.peek()?.t === "comma") {
            this.next();
            args.push(this.comparison());
          }
        }
        const close = this.next();
        if (close.t !== "rp") throw new Error("expected ) after args");
        if (!Object.prototype.hasOwnProperty.call(FUNCS, tok.v))
          throw new Error(`unknown function '${tok.v}'`);
        return FUNCS[tok.v](args);
      }
      if (!Object.prototype.hasOwnProperty.call(this.vars, tok.v))
        throw new Error(`unknown variable '${tok.v}'`);
      return this.vars[tok.v];
    }
    throw new Error("unexpected token in primary");
  }
}

export function evalFormula(src: string, vars: Vars): number {
  try {
    const r = new Parser(tokenize(src), vars).parse();
    return typeof r === "number" ? r : NaN;
  } catch {
    return NaN;
  }
}
